/**
 * 加载页（原站 m 组件 / C3C9Bl + fbcMXS + MnIXwn + ruslc）。
 *
 * 时间轴（原站 gsap，单位 ms，相对挂载）：
 *   0      圆环 strokeDashoffset 630 -> 0，1600ms，easeOutCubic
 *   1600   进度槽淡入，200ms linear
 *   1800   百分比文字淡入，200ms linear
 *   2000   计数补间：v 从 0 -> 1，时长 30*max(progress,10) ms，easeOutQuart，逐帧写：
 *             bar.style.transform = scaleY(v * progress / 100)
 *             label.style.bottom  = v * clamp(progress, 4, 96) + '%'
 *             label.innerText     = floor(v * progress) + '%'
 *   progress = 12（本次抓取到的值）=> 时长 360ms，终值 "12%"，进度槽 scaled 0.12
 *
 * 注意：只有 /meta 返回 pass 为假时才会进入这个页面。
 */
import { useEffect, useRef, useState } from 'react';
import { COPY } from '../../types';
import './splash.css';

const RING_PATH = 'M 100, 100 m 0, -100 a 100,100 0 1,0 0,200 a 100,100 0 1,0 0,-200';

const RING_MS = 1600;
const BAR_FADE_AT = 1600;
const LABEL_FADE_AT = 1800;
const COUNT_START = 2000;

/** 原站的进度值夹取区间 */
const clampProgress = (p: number) => Math.max(Math.min(p, 96), 4);

export interface SplashProps {
  /** 来自 /meta 的 data.progress */
  progress: number;
}

export function Splash({ progress }: SplashProps) {
  const [barVisible, setBarVisible] = useState(false);
  const [labelVisible, setLabelVisible] = useState(false);
  const [v, setV] = useState(0);
  const raf = useRef<number | null>(null);

  useEffect(() => {
    const t1 = window.setTimeout(() => setBarVisible(true), BAR_FADE_AT);
    const t2 = window.setTimeout(() => setLabelVisible(true), LABEL_FADE_AT);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, []);

  // 计数补间：0 -> 1，时长 30 * max(progress, 10) ms，easeOutQuart
  useEffect(() => {
    const duration = 30 * Math.max(progress, 10);
    let start: number | null = null;
    const tick = (now: number) => {
      if (start === null) start = now;
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 4); // easeOutQuart
      setV(eased);
      if (t < 1) raf.current = requestAnimationFrame(tick);
    };
    const t = window.setTimeout(() => {
      raf.current = requestAnimationFrame(tick);
    }, COUNT_START);
    return () => {
      window.clearTimeout(t);
      if (raf.current !== null) cancelAnimationFrame(raf.current);
    };
  }, [progress]);

  const barScale = Math.max(0, (v * progress) / 100);
  const labelBottom = `${v * clampProgress(progress)}%`;
  const pct = Math.floor(v * progress);

  return (
    <div className="loading">
      <div className="loadingCycle">
        <svg viewBox="-2 -2 204 204">
          <path
            className="drawNow"
            stroke="#fff"
            strokeWidth={0.5}
            strokeDasharray={630}
            strokeDashoffset={630}
            d={RING_PATH}
            style={{ animationDelay: '0ms', animationDuration: `${RING_MS}ms` }}
          />
        </svg>
      </div>
      <div className="loadingProgress" style={{ opacity: barVisible ? 1 : 0 }}>
        <div className="loadingProgressBar" style={{ transform: `scaleY(${barScale})` }} />
        <span className="loadingProgressCount" style={{ opacity: labelVisible ? 1 : 0, bottom: labelBottom }}>
          {pct}%
        </span>
      </div>
      <span className="srOnly" role="status">
        {COPY.connecting}
      </span>
    </div>
  );
}
